"use client";

import Link from "next/link";
import { type ServiceContent } from "../../prestations/data/types";
import { LIArrowDown } from "@/assets/icons/li-arrow-down";
import { MyNauiLongArrowRight } from "@/assets/icons/mynaui-long-arrow-right";
import { getVideoUrl } from "@/lib/cloudinary";

interface HeadSpaIntroSectionProps {
  aboveTitle: string;
  title: string;
  subtitle: string;
  content: ServiceContent[];
}

const PLANITY_URL = "https://www.planity.com/au-grey-des-couleurs-94800-villejuif";

export const HeadSpaIntroSection = ({
  aboveTitle,
  title,
  subtitle,
  content,
}: HeadSpaIntroSectionProps) => {
  const scrollToFeaturedService = () => {
    const featuredServiceSection = document.getElementById("featured-service");
    if (featuredServiceSection) {
      featuredServiceSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative w-full bg-white py-0">
      {/* Vidéo plein écran avec texte superposé */}
      <div className="relative h-screen w-full">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 h-full w-full object-cover brightness-[0.6]"
        >
          <source src={getVideoUrl("head-spa-hero")} type="video/mp4" />
        </video>

        {/* Overlay sombre uniforme, cohérent avec la hero section du site */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(rgba(25, 25, 25, 0.6), rgba(25, 25, 25, 0.6))",
          }}
        ></div>

        {/* Titre superposé sur la vidéo - même typo que la hero */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-4 text-center">
          <span className="text-md uppercase tracking-wider text-white ph:text-lg">
            {aboveTitle}
          </span>
          <h2 className="mb-[-0.5rem] font-cormorant text-4xl uppercase leading-[0.9] tracking-wider text-white xs:text-5xl ph:mb-[-1rem] ph:text-6xl sm:text-7xl md:mb-[-1.5rem] md:text-8xl lg:text-9xl">
            {title}
          </h2>
          <p className="mt-4 max-w-2xl text-md text-white/90 ph:text-lg md:mt-10 lg:mt-12">
            {subtitle}
          </p>
          <Link
            href={PLANITY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-4 relative inline-flex w-fit items-center gap-2 overflow-hidden border border-white bg-transparent px-8 py-3 text-base font-medium text-white transition-colors ph:text-lg md:mt-2"
          >
            <span className="relative z-10 transition-colors group-hover:text-primary">
              Je réserve un soin
            </span>

            <div className="absolute bottom-0 left-0 h-full w-0 bg-white transition-all duration-300 ease-out group-hover:w-full" />
          </Link>
        </div>

        {/* Flèche scroll - identique à la hero section */}
        <div
          onClick={scrollToFeaturedService}
          role="button"
          aria-label="Défiler vers le soin mis en avant"
          className="absolute bottom-12 left-1/2 flex -translate-x-1/2 flex-col items-center justify-center text-white"
        >
          <div className="scroll-down-wrapper flex flex-col items-center justify-center">
            <LIArrowDown className="relative z-10 cursor-pointer" width={38} />
          </div>
        </div>
      </div>


    </section >
  );
};
